import React, { createContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import ModalBody from '../ModalBody';
import EditTokensView from './EditTokensView';
import ConfirmTokensChanges from './ConfirmTokensChanges';
import UpdatedHPoolList from './UpdatedHPoolList';
import {
  EDIT_TOKEN_VIEW_STAGE,
  CONFIRM_TOKENS_CHANGES_STAGE,
  EDIT_ORDER_DETAILS_STAGE_INDEX,
  UPDATED_HPOOL_LIST_STAGE,
} from '../../../constants/hpool';
import { replaceHpoolAddedTokens } from '../../../redux/actions/createHPoolActions';
import Stepper from '../../Common/Stepper/Stepper';
import EditOrderDetails from './EditOrderDetails';
import NavigationControls from './NavigationControls/NavigationControls';
import { getValueByMultiProps, objectMapper } from '../../../utils/mapper';
import { usePromise } from '../../../hooks/usePromise/usePromise';
import { getOpenOrdersReq, updatePoolStatsReq } from '../../../services/hpoolService';
import { useOnce } from '../../../hooks/useOnce';
import config from '../../../config/config.json';
import { getTokenSymbol } from '../../../utils/format';
import { getBaseToken } from '../../../services/createHPoolService';
// import EditTokenStep from './EditTokenStep/EditTokenStep';

export const OpenOrdersContext = createContext({
  getOpenOrders: () => {},
  openOrders: {},
  openOrdersLoading: false,
});

const EditTokensModal = ({
  closeModal,
  addedTokens,
  replaceHpoolAddedTokens,
  hpool /* stage: initialStage, pendingTradeOrders */,
  initialStep,
}) => {
  const [updateStats, updateRes, updateStatsLoading] = usePromise(updatePoolStatsReq, { sleep: 3000 });
  const baseCoinName = getTokenSymbol(getBaseToken(window.configTokens)?.toUpperCase?.() || {});

  const [_getOpenOrders, openOrders, loading] = usePromise(getOpenOrdersReq);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [updateStatsCalled, setUpdateStatsCalled] = useState(false);

  const mappedTokens = useMemo(
    // () => (pendingTradeOrders || addedTokens)?.map((token) => objectMapper(token, { current_ratio_from_pool: 'percentage' })),
    () => addedTokens?.map((token) => objectMapper(token, { current_ratio_from_pool: 'percentage' })) || [],
    [addedTokens],
  );
  const [modifiedModalTokens = [], setModifiedModalTokens] = useState(mappedTokens);
  const [selectedToken, setSelectedToken] = useState(null);

  const percentageKeys = useMemo(
    () => modifiedModalTokens.map((token) => [token?.percentage, ''].join('')).join(''),
    [modifiedModalTokens],
  );

  // starting stage has index of 1 in case of empty navHistory
  const stage = !navigationHistory.length ? 1 : navigationHistory?.[navigationHistory.length - 1];

  useEffect(() => {
    if (!updateStatsCalled && stage === EDIT_TOKEN_VIEW_STAGE) {
      updateStats(hpool.contract_address);
      setUpdateStatsCalled(true);
    }
  }, [updateStats, hpool.contract_address, updateStatsCalled, stage]);

  const popHistory = () => {
    const _history = navigationHistory;
    _history.pop();
    setNavigationHistory([..._history]);
  };
  const pushHistory = (stage) => {
    setNavigationHistory([...navigationHistory, stage]);
  };

  const onNextStage = async (passedStage) => {
    if (stage === EDIT_TOKEN_VIEW_STAGE) {
      replaceHpoolAddedTokens(modifiedModalTokens);
    }
    const _stage = +passedStage || stage;
    pushHistory(_stage + 1);
  };

  const onPrevStage = () => {
    popHistory();
  };

  const moveToTokenEdit = (token) => {
    setSelectedToken(token);
    onNextStage(EDIT_ORDER_DETAILS_STAGE_INDEX);
  };
  const getOpenOrders = () => {
    _getOpenOrders(hpool.id);
  };

  useEffect(() => {
    if (hpool.id) getOpenOrders();
  }, [hpool]);

  useEffect(() => {
    if (initialStep) {
      pushHistory(initialStep);
    }
  }, [initialStep]);

  const getTokenSybmol = (token) => getValueByMultiProps(token, ['symbol', 'token_symbol']);

  const baseTokensSymbols = useMemo(() => mappedTokens.map(getTokenSybmol) || [], [mappedTokens]);

  const newAddedTokensSymbols = useMemo(
    () => modifiedModalTokens.filter((token) => !baseTokensSymbols.includes(getTokenSybmol(token))).map(getTokenSybmol),
    [modifiedModalTokens],
  );

  const getPercentage = (token = {}) => token.percentage;

  const tokensHasChanges = useMemo(
    () => !_.isEqual(mappedTokens.map(getPercentage), modifiedModalTokens.map(getPercentage)),
    [JSON.stringify(modifiedModalTokens), JSON.stringify(mappedTokens)],
  );

  const ordersContextValue = { getOpenOrders, openOrders, openOrdersLoading: loading };

  const modalTitle = {
    0: 'modal.edit_limit_order',
    1: 'modal.edit_tokens',
    2: 'modal.edit_tokens',
    3: 'updated_hpool_list',
  }?.[stage];

  return (
    <div className="edit-tokens-outer-container">
      <div
        className={
          stage === EDIT_TOKEN_VIEW_STAGE
            ? 'edit-tokens-container-medium'
            : stage === CONFIRM_TOKENS_CHANGES_STAGE
            ? 'edit-tokens-container-small'
            : 'edit-tokens-container-large'
        }
      >
        <ModalBody title={modalTitle} closeModal={closeModal} onBack={!stage && onPrevStage}>
          <OpenOrdersContext.Provider value={ordersContextValue}>
            <Stepper currentStepIndex={stage}>
              <Stepper.Step>
                <EditOrderDetails selectedToken={selectedToken} onPrev={onPrevStage} />
              </Stepper.Step>
              <Stepper.Step>
                <EditTokensView
                  onNextStage={onNextStage}
                  onTokenEdit={moveToTokenEdit}
                  stage={stage}
                  modifiedModalTokens={modifiedModalTokens}
                  setModifiedModalTokens={setModifiedModalTokens}
                  disabledTokens={[baseCoinName, ...newAddedTokensSymbols]}
                  baseTokens={mappedTokens}
                />
                <NavigationControls
                  disabled={(!updateRes && stage !== EDIT_TOKEN_VIEW_STAGE) || !tokensHasChanges}
                  onNext={onNextStage}
                  loading={updateStatsLoading}
                  laodingTitle="button.next"
                />
              </Stepper.Step>
              <Stepper.Step>
                <ConfirmTokensChanges
                  onNextStage={onNextStage}
                  stage={stage}
                  onPrevStage={onPrevStage}
                  modifiedModalTokens={modifiedModalTokens}
                  setModifiedModalTokens={setModifiedModalTokens}
                  baseTokens={mappedTokens}
                />
              </Stepper.Step>
              <Stepper.Step>
                <UpdatedHPoolList
                  onPrevStage={onPrevStage}
                  modifiedModalTokens={modifiedModalTokens}
                  baseTokens={mappedTokens}
                />
              </Stepper.Step>
            </Stepper>
          </OpenOrdersContext.Provider>
        </ModalBody>
      </div>
    </div>
  );
};

EditTokensModal.propTypes = {
  addedTokens: PropTypes.array.isRequired,
  hpool: PropTypes.object.isRequired,
  closeModal: PropTypes.func.isRequired,
  replaceHpoolAddedTokens: PropTypes.func.isRequired,
  initialStep: PropTypes.number,
};

EditTokensModal.defaultProps = {
  initialStep: 0,
};
const mapStateToProps = ({ createHPool, modal }) => ({
  addedTokens:
    (Array.isArray(createHPool?.hpool?.assets) && createHPool?.hpool?.assets) || createHPool.addedTokens || [],
  hpool: createHPool?.hpool,
  initialStep: modal.modalProps.initialStep,
});

const mapDispatchToProps = {
  replaceHpoolAddedTokens,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditTokensModal);
